/**
 * Client HTTP minimal vers l'API Revi (/api/v1/).
 * Sessions par cookie + CSRF (§17.5) : le jeton CSRF est lu dans le cookie Django.
 */

const API_BASE = "/api/v1";

function getCookie(name: string): string | undefined {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`Erreur API ${status}`);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (method !== "GET" && method !== "HEAD") {
    if (!(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
    if (!getCookie("csrftoken")) {
      await fetch(API_BASE + "/auth/session/", { credentials: "same-origin" });
    }
    const csrf = getCookie("csrftoken");
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: "same-origin" });
  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

export function post<T>(path: string, data: unknown = {}): Promise<T> {
  return api<T>(path, { method: "POST", body: JSON.stringify(data) });
}

export function patch<T>(path: string, data: unknown): Promise<T> {
  return api<T>(path, { method: "PATCH", body: JSON.stringify(data) });
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const collect = (value: unknown): string[] => {
      if (typeof value === "string") return [value];
      if (Array.isArray(value)) return value.flatMap(collect);
      if (value && typeof value === "object") return Object.values(value).flatMap(collect);
      return [];
    };
    return collect(error.body).join(" ") || "La demande n'a pas abouti. Réessayez.";
  }
  return "Connexion interrompue. Vérifiez votre réseau et réessayez.";
}

export interface HealthResponse {
  status: "ok" | "degraded";
  checks: { database: boolean; cache: boolean };
}

export const getHealth = () => api<HealthResponse>("/health/");
